import C19_PromotionalArticles, {
  ArticlePromotionType,
} from "@components/C19_PromotionalArticles/C19_PromotionalArticles";
import { TypeC194PromotionalResearchFields } from "@customTypes/contentful";
import { fetchApi } from "@utilities/fetchApi";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import styles from "./C19.4_PromotionalResearch.module.scss";

export interface PromotionalResearchProps
  extends TypeC194PromotionalResearchFields {
  tags?: string[];
}

const PromotionalResearch = ({
  content,
}: {
  content: PromotionalResearchProps;
}) => {
  const {
    researchHighlights,
    title,
    isBlue,
    selectedAuthor,
    numberOfItems,
    tags,
  } = content;

  const [fetchedPromo, setFetchedPromo] = useState<ArticlePromotionType[]>([]);
  const tagIds = tags || [];
  const { asPath } = useRouter();

  const promoFromHighlights = useMemo(() => {
    if (!researchHighlights?.length) return null;

    return researchHighlights.map((research) => ({
      title: research.fields.researchTitle,
      description: research.fields.basePageTemplate?.pageDescription || "",
      image: research.fields.thumbnailImage || null,
      authors:
        research.fields.authorsList?.map(
          ({ fields: { articleAuthorName } }) => articleAuthorName
        ) || null,
      slug: research.fields.slug,
      isVideo: Boolean(research.fields.video?.videoCode),
    }));
  }, [researchHighlights]);

  useEffect(() => {
    if (promoFromHighlights) return;
    if ((!tagIds.length && !selectedAuthor) || !numberOfItems) return;

    let cancelled = false;

    const load = async () => {
      const omitSlug = asPath.split("#")[0].split("?")[0].slice(1);
      const [tagResults, authorResults] = await Promise.all([
        tagIds.length
          ? fetchApi("/api/getResearchHighlightsByTags", {
              tagIDs: tagIds.join(","),
              limit: numberOfItems.toString(),
              omitSlug,
            })
          : [],
        selectedAuthor?.fields?.articleAuthorName
          ? fetchApi("/api/getResearchHighlightByAuthor", {
              selectedAuthor: selectedAuthor.fields.articleAuthorName,
              limit: numberOfItems.toString(),
            })
          : [],
      ]);
      const merged = [...tagResults, ...authorResults];

      const unique = merged.filter(
        (article, index, self) =>
          index === self.findIndex((a) => a.slug === article.slug)
      );

      const sorted = unique.sort(
        (a, b) => Date.parse(b.date) - Date.parse(a.date)
      );

      if (!cancelled) {
        setFetchedPromo(sorted?.slice(0, numberOfItems));
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [promoFromHighlights, selectedAuthor, numberOfItems, tags, asPath]);

  const promo = promoFromHighlights ?? fetchedPromo;

  return (
    <div className={clsx(isBlue ? styles.blue : styles.defaultbgColor)}>
      <div className="wrapper">
        <h2 className={styles.title}>{title}</h2>
      </div>

      <C19_PromotionalArticles
        articles={promo.map((article) => ({
          ...article,
          subtitle: "RESEARCH HIGHLIGHTS",
        }))}
        isBlue={isBlue}
        ctaText="Find out more"
      />
    </div>
  );
};

export default PromotionalResearch;
